import { useRef } from "react"

// ** Third Party Components
import { useVirtualizer } from "@tanstack/react-virtual"

// ** UI Components
import PmsLoader from "@components/common/loader"

// ** Types
import { DictType } from "@pma/types"

// ** Helpers
import { cn, getFileDisplayType } from "@pma/utils/helpers"

// ** Constants
import { IMAGE_EXTENSIONS } from "@pma/utils/constants"

// ** Custom Hooks
import useTranslator from "@hooks/use-translator"
import { useFilesView } from "@pma/hooks/use-files-view"

// ** Custom Components
import RenderFileIcon from "./render-file-icon"
import FileContextMenu from "./file-context-menu"
import { FilePreview } from "@components/dashboard/preview-file"

/** Estimated height per row in pixels */
const ESTIMATED_ROW_HEIGHT = 72

interface VirtualListViewProps {
  showAsModal?: boolean
}

export default function VirtualListView({ showAsModal = false }: VirtualListViewProps) {
  const { translate } = useTranslator()
  const {
    activeFolder,
    folderItems,
    showBatchSelection,
    filterActive,
    setClearFilter,
    setNewActiveData,
    handleTreeItemFolderClick,
    isLoading,
    isFetching,
    nextPage,
    handleLoadMoreData,
  } = useFilesView()

  const items: DictType[] = folderItems[activeFolder?.id] ?? []
  const parentRef = useRef<HTMLDivElement>(null)

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    overscan: 5,
    measureElement:
      typeof window !== "undefined" && navigator.userAgent.indexOf("Firefox") === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
  })

  const containerHeight =
    showAsModal && window?.innerWidth > 1024
      ? "calc(100% - 5rem)"
      : window?.innerWidth < 425
        ? "calc(100% - 15rem)"
        : "calc(100% - 10rem)"

  if (isLoading || isFetching) {
    return <PmsLoader className='min-h-[7vh]' />
  }

  const handleClick = (item: any) => {
    if (item) {
      document.getElementById(`check-${item.id}`)?.click()
    }
  }

  return (
    <div
      ref={parentRef}
      className='mt-2 overflow-y-auto'
      style={{ height: containerHeight }}
      onScroll={(e) => {
        const el = e.currentTarget
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 200 && nextPage) {
          handleLoadMoreData()
        }
      }}
    >
      <div className='relative w-full' style={{ height: `${virtualizer.getTotalSize()}px` }}>
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const item = items[virtualRow.index]
          if (!item) return null

          return (
            <div
              key={virtualRow.key}
              ref={virtualizer.measureElement}
              data-index={virtualRow.index}
              className='absolute left-0 top-0 w-full'
              style={{
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              <div
                className='group relative m-1 flex cursor-pointer items-center justify-between space-x-4 rounded-2xl border border-gray-200 bg-white p-4 shadow dark:border-gray-700 dark:bg-gray-800'
                onClick={() => {
                  if (item.as === "folder") {
                    if (!filterActive) handleTreeItemFolderClick(item, true)
                    else {
                      setClearFilter(true)
                      setNewActiveData(item)
                    }
                  }
                }}
              >
                <div className='flex items-center space-x-4 gap-1'>
                  {item.as === "folder" ? (
                    <RenderFileIcon styleClass='h-10 w-10' type={getFileDisplayType(item)} />
                  ) : (
                    <FilePreview
                      file={item}
                      isSelectionOn={showBatchSelection}
                      onClick={() => handleClick(item)}
                    >
                      {IMAGE_EXTENSIONS.includes(item.type) ? (
                        <img
                          src={item?.file}
                          alt='image'
                          className='h-10 w-10 rounded object-cover'
                          loading='lazy'
                        />
                      ) : (
                        <RenderFileIcon styleClass='h-10 w-10' type={getFileDisplayType(item)} />
                      )}
                    </FilePreview>
                  )}

                  <div className='max-w-[calc(100vw-12rem)] truncate'>
                    <p
                      className={cn(
                        "truncate text-sm font-medium text-gray-900 dark:text-white",
                        item?.deleted ? "text-red-500" : "",
                      )}
                    >
                      {item.name}
                    </p>
                    {item.as === "folder" && (
                      <p className='text-xs capitalize text-gray-500'>
                        {translate("files")}:{" "}
                        {item._files_in_folder > 99
                          ? `+${item._files_in_folder}`
                          : item._files_in_folder}
                      </p>
                    )}
                    <p className='text-xs capitalize text-gray-500'>{translate(item.as)}</p>
                  </div>
                </div>

                {item.as !== "folder" && <FileContextMenu item={item} variant='vertical' />}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
